import { createFormField, FormFieldType, FormModel, type FileConfig, type FormField, type PostForm, type PostFormFile } from '@/models/form';
import { LoadingStateModel } from '@/models/loadingState';
import formService from '@/services/formService';
import BlobService from '@/services/blobService';
import { generateHexIdentifier } from '@/utils';
import { defineStore } from 'pinia';
import { computed, reactive, ref } from 'vue';
import { useStudyStore } from '@/stores/study';
import { useFormStore } from '@/stores/forms';
import { readCSVFile } from '@/components/forms/form_builder/FormFieldConfigHelper';
import { cloneDeep } from 'lodash';

export const useFormBuilder = defineStore('formBuilderStore', () => {
  const studyStore = useStudyStore();
  const formStore = useFormStore();

  const form = ref<FormModel>();
  const initialForm = ref<FormModel>();

  const getFormLoader = reactive(new LoadingStateModel({isLoading: false}));
  const getFormDetails = async (identifier: string) => {
    try {
      form.value = undefined;
      getFormLoader.startLoading();
      form.value = await formService.getForm(identifier);
      initialForm.value = cloneDeep(form.value);
    } catch (error) {
      getFormLoader.setError(error);
    } finally {
      getFormLoader.stopLoadingAndFinish();
    }
  };

  const addFormField = (field: FormField) => {
    const fieldIndex = form.value?.items?.findIndex(item => item.identifier == field.identifier);
    if (fieldIndex !== undefined && fieldIndex > -1) {
      const newField = createFormField();
      form.value?.items?.splice(fieldIndex + 1, 0, newField);

      let sectionField: FormField | undefined;
      //check if the added field is after section field, then add sectionId to the newly added field
      for (let i = fieldIndex; i >= 0; i--) {
        const currentField = form.value!.items![i];
        if (currentField.type === FormFieldType.SECTION) {
          sectionField = currentField;
          break;
        }
      }

      if (sectionField) {
        newField.sectionId = sectionField.identifier;
        if (!sectionField.options.fields) {
          sectionField.options.fields = [];
        }
        sectionField.options.fields.push(newField.identifier);
      }
    }
  };

  const deleteFormField = (field: FormField) => {
    if (form.value?.items?.length == 1) {
      const errorObject: Error = {
        name: 'NotAllowedException',
        message: 'Cannot delete the last field'
      };
      getFormLoader.setError(errorObject);
      return;
    }
    const fieldIndex = form.value?.items?.findIndex(item => item.identifier == field.identifier);
    if (fieldIndex !== undefined && fieldIndex > -1) {
      form.value?.items?.splice(fieldIndex, 1);

      let sectionField: FormField | undefined;
      for (let i = fieldIndex - 1; i >= 0; i--) {
        const currentField = form.value!.items![i];
        if (currentField.type === FormFieldType.SECTION) {
          sectionField = currentField;
          break;
        }
      }

      if (sectionField) {
        sectionField.options.fields = sectionField.options.fields?.filter(id => id !== field.identifier);
      }
    }
  };

  const cloneFormField = (field: FormField) => {
    const fieldIndex = form.value?.items?.findIndex(item => item.identifier == field.identifier);
    if (fieldIndex !== undefined && fieldIndex > -1) {
      const clonedField = cloneDeep(field);
      clonedField.identifier = generateHexIdentifier(6);
      form.value?.items?.splice(fieldIndex + 1, 0, clonedField);
    }
  };

  const handleFieldTypeChange = (updatedField: FormField) => {
    const allItems = form.value!.items!;
    const fieldIndex = allItems.findIndex(item => item.identifier === updatedField.identifier);

    if (fieldIndex === -1) return;

    //section field handling logic

    if (updatedField.type === FormFieldType.SECTION) {
      const originalField = allItems[fieldIndex];
      const wasSection = originalField.type === FormFieldType.SECTION;
      const isSection = updatedField.type === FormFieldType.SECTION;

      // Find the original parent section of the field being changed.
      let parentSection: FormField | undefined;
      if (originalField.sectionId) {
        parentSection = allItems.find(item => item.identifier === originalField.sectionId);
      }

      // If the field was a section, its children need to be reassigned.
      if (wasSection && !isSection) {
        const childrenToReassign = originalField.options.fields || [];
        childrenToReassign.forEach(childId => {
          const childField = allItems.find(item => item.identifier === childId);
          if (childField) {
            childField.sectionId = parentSection?.identifier;
          }
        });
        // Add re-assigned children to the parent section's field list
        if (parentSection) {
          const parentChildren = parentSection.options.fields || [];
          parentSection.options.fields = [...parentChildren, ...childrenToReassign];
        }
        // Clear the options from the now non-section field
        originalField.options.fields = undefined;
      }

      // Update the field in the form.
      allItems[fieldIndex] = { ...originalField, ...updatedField };
      const currentField = allItems[fieldIndex];

      // If the field is now a section, it needs to adopt subsequent fields.
      if (isSection) {
        // A section cannot be a child of another section.
        currentField.sectionId = undefined;
        if (parentSection) {
          // Remove the new section and all fields after it from the original parent.
          const newParentFields = parentSection.options.fields || [];
          const sliceIndex = newParentFields.indexOf(currentField.identifier);
          if (sliceIndex > -1) {
            parentSection.options.fields = newParentFields.slice(0, sliceIndex);
          }
        }

        const childFields: string[] = [];
        for (let i = fieldIndex + 1; i < allItems.length; i++) {
          const subsequentField = allItems[i];
          if (subsequentField.type === FormFieldType.SECTION) {
            break; // Stop at the next section
          }
          subsequentField.sectionId = currentField.identifier;
          childFields.push(subsequentField.identifier);
        }
        currentField.options.fields = childFields;
      } else {
        // If it's not a section, ensure it belongs to the correct parent section.
        currentField.sectionId = parentSection?.identifier;
      }
    }
  };

  const patchForm = async (navigate?: boolean) => {
    if (navigate) {
      openDiscardChangesAlert.value = !openDiscardChangesAlert.value;
    }
    getFormLoader.startLoading();

    // removing drill levels from the post data
    const items: FormField[] = cloneDeep(form.value!.items!);
    items.forEach(item => {
      if (item.type === FormFieldType.DRILLDOWN) {
        item.options.drillLevels = undefined;
        item.options.blob = undefined;
      } else if (item.type === FormFieldType.FILE) {
        if ((item.options.allowedExtensions as FileConfig).enabled) {
          item.options.allowedExtensions = (item.options.allowedExtensions as FileConfig).value as string;
        } else {
          delete item.options.allowedExtensions;
        }

        if ((item.options.maximumFileNumber as FileConfig).enabled) {
          item.options.maximumFileNumber = (item.options.maximumFileNumber as FileConfig).value as number;
        } else {
          delete item.options.maximumFileNumber;
        }

        if ((item.options.maximumSize as FileConfig).enabled) {
          item.options.maximumSize = (item.options.maximumSize as FileConfig).value as number;
        } else {
          delete item.options.maximumSize;
        }
      } else if (item.type === FormFieldType.CHOICE || item.type === FormFieldType.DROPDOWN) {
        if (item.options.maximumChoicesCount === 2) {
          delete item.options.maximumChoicesCount;
        }
      } else if (item.type === FormFieldType.TEXT || item.type === FormFieldType.NUMBER) {
        //removing unwanted options from TEXT and NUMBER as these field doesn't have any configuration options
        item.options = {};
      }
    });
    const postForm: PostForm = {
      title: form.value!.title,
      studies: [{
        identifier: studyStore.selectedStudyId!,
        scrambled: true
      }],
      revisionDate: new Date().getTime() / 1000,
      items: items,
      anonymousResponsesAllowed: form.value!.anonymousResponsesAllowed,
      reminders: form.value?.reminders,
      editExistingEntry: form.value?.editExistingEntry
    };

    try {
      await formService.patchForm(form.value!.identifier, postForm);
      getFormLoader.setSuccess('Form updated successfully.');
      getFormDetails(form.value!.identifier);
      formStore.getStudyForms();

      if (navigate) {
        resolveFn.value();
        resolveFn.value = undefined;
      }
    } catch (error) {
      getFormLoader.setError(error);
    } finally {
      getFormLoader.stopLoadingAndFinish();
    }
  };

  const drilldownFileLoader = reactive(new LoadingStateModel({isLoading: false}));
  const getSignedUrl = async (file: File, field: FormField) => {
    try {
      drilldownFileLoader.startLoading();
      const csvData = await readCSVFile(file);
  
      if (!csvData.error) {
        const csvFile = csvData.file!;
        const fileData: PostFormFile = {
          filename: csvFile.name,
          filetype: csvFile.type,
          size: csvFile.size,
          withoutItemId: true
        };
        const presignedData = await formService.postFormFile(form.value!.identifier, fileData);
        await formService.uploadFileToS3(csvFile, presignedData.presignedPost);
        const blobData = await BlobService.patchBlob(presignedData.blob.identifier);
        const fieldIndex = form.value?.items?.findIndex(formField => formField.identifier == field.identifier);
        if (fieldIndex !== undefined && fieldIndex > -1) {
          form.value!.items![fieldIndex].options.drillLevels = csvData.drillLevels;
          form.value!.items![fieldIndex].options.blob = blobData;
          form.value!.items![fieldIndex].options.file = blobData.identifier;
        }
      } else {
        // show error
        const errorObject: Error = {
          name: 'ParseException',
          message: csvData.error
        };
        drilldownFileLoader.setError(errorObject);
      }
    } catch (error) {
      drilldownFileLoader.setError(error);
    } finally {
      drilldownFileLoader.stopLoadingAndFinish();
    }
  };


  const isFormPristine = computed(() => {
    if (!initialForm.value || form.value?.items?.length == 0 || !form.value) {
      return true;
    }
    const existingForm: FormModel = cloneDeep(initialForm.value);
    existingForm.items?.forEach(item => delete item.expanded);
    const updatedForm: FormModel = cloneDeep(form.value);
    updatedForm.items?.forEach(item => delete item.expanded);

    return JSON.stringify(existingForm) === JSON.stringify(updatedForm);
  });

  const openDiscardChangesAlert = ref(false);
  const showOnlyDiscardViews = ref(false);
  const resolveFn = ref();
  const rejectFn = ref();

  const showDiscardChangesDialog = async () => {
    openDiscardChangesAlert.value = !openDiscardChangesAlert.value;

    return new Promise((resolve, reject) => {
      resolveFn.value = resolve;
      rejectFn.value = reject;
    });
  };

  const discardFormChanges = () => {
    openDiscardChangesAlert.value = !openDiscardChangesAlert.value;
    showOnlyDiscardViews.value = false;
    form.value = undefined;
    setTimeout(() => {
      // Adding a timeout since the `form.value = undefined` alone wasn't helping with the reactivity.
      form.value = cloneDeep(initialForm.value);
    }, 1);
    resolveFn.value();
    resolveFn.value = undefined;
  };

  const cancelDiscardChangesDialog = () => {
    openDiscardChangesAlert.value = !openDiscardChangesAlert.value;
    showOnlyDiscardViews.value = false;
    rejectFn.value();
    rejectFn.value = undefined;
  };

  return {
    getFormDetails,
    addFormField,
    deleteFormField,
    cloneFormField,
    patchForm,
    getSignedUrl,
    showDiscardChangesDialog,
    discardFormChanges,
    cancelDiscardChangesDialog,
    handleFieldTypeChange,
    getFormLoader,
    drilldownFileLoader,
    openDiscardChangesAlert,
    showOnlyDiscardViews,
    isFormPristine,
    form,
    initialForm
  };
});
